import type { Executor } from "@pdfking/planner"
import type { ExecutionContext } from "@pdfking/taskmaker"
import type { Partial, StringGUID } from "@pdfking/tfunc"
import { AtomHash, GuidToString } from "@pdfking/tfunc"

import type { PageOrder } from "#public"
import type { VirtualFile } from "#public"
import type { TasksType } from "#tasks"

import { FileContentGUID } from "./FileContentGUID"

/**
 * A file's identity as a plan literal — what {@link FileLiteral} and
 * {@link FileArray} pass instead of letting the planner hash the value's JSON.
 *
 * {@link FileContentGUID} already answers "which bytes is this": a guid for a
 * real file, its parts' guids for a lazy one. Working that out by
 * re-serializing the file walks everything underneath it, which for a merged
 * document is the entire document — once per page, at every level it is
 * embedded, which is how a 426-page imposition reached half a gigabyte of JSON
 * per hash.
 *
 * `page_order` rides along because the engine relabels it when it places a
 * page in a merge (see SplitNode/mergeNode): two literals over the same bytes
 * at different positions are genuinely different nodes, and their hashes reach
 * the enclosing task's cache key, so collapsing them would let one merge be
 * served another merge's ordering from cache. It lives here rather than in
 * `FileContentGUID` because position is a property of this node, not of the
 * file — the blob-url and download callers key on content alone and should
 * keep sharing a hit across positions.
 */
export const FileNodeGUID = (file: VirtualFile & Partial<PageOrder>): StringGUID => {
  const guid = FileContentGUID(file)
  // Position 0 is the unplaced page, which is most of them — hand back the
  // content guid untouched rather than pay a second hash to fold in a zero.
  return file.page_order ? GuidToString(AtomHash([guid, file.page_order])) : guid
}

type Plan = Executor<TasksType, ExecutionContext>["plan"]

// Every file node goes through these, never raw `plan.Literal`/`plan.Array`:
// the planner's default hash (JSON of the value) re-serializes the file's
// embedded plan tree (see FileNodeGUID), and a forgotten `hash` fails silently
// — as a quiet O(document) hash on a hot path.
export const FileLiteral = <T extends VirtualFile & Partial<PageOrder>>(plan: Plan, file: T) =>
  plan.Literal({ value: file, hash: FileNodeGUID(file) })

export const FileArray = <T extends VirtualFile & Partial<PageOrder>>(plan: Plan, files: T[]) =>
  plan.Array({ values: files, hash: GuidToString(AtomHash(files.map(FileNodeGUID))) })
